/**
 * Multi-format audio encoder — WAV (RIFF) and AIFF at 16/24/32-bit PCM.
 * No external dependencies.
 */

export type AudioFormat = 'wav' | 'aiff'
export type BitDepth = 16 | 24 | 32

interface EncodeOptions {
  format: AudioFormat
  bitDepth: BitDepth
  sampleRate: number
  channels: Float32Array[]
}

/**
 * Encode an AudioBuffer's channel data to a WAV or AIFF Blob.
 */
export function encodeAudio(opts: EncodeOptions): Blob {
  return opts.format === 'wav' ? encodeWav(opts) : encodeAiff(opts)
}

/**
 * Get the file extension for a given format.
 */
export function formatExtension(format: AudioFormat): string {
  return format === 'wav' ? 'wav' : 'aif'
}

/**
 * Get the MIME type for a given format.
 */
export function formatMime(format: AudioFormat): string {
  return format === 'wav' ? 'audio/wav' : 'audio/aiff'
}

// ---------------------------------------------------------------------------
// WAV (RIFF, little-endian)
// ---------------------------------------------------------------------------

function encodeWav({ bitDepth, sampleRate, channels }: EncodeOptions): Blob {
  const numChannels = channels.length
  const numSamples = channels[0].length
  const bytesPerSample = bitDepth / 8
  const dataSize = numSamples * numChannels * bytesPerSample
  const headerSize = 44
  const buffer = new ArrayBuffer(headerSize + dataSize)
  const view = new DataView(buffer)

  // RIFF header
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true) // file size - 8
  writeString(view, 8, 'WAVE')

  // fmt chunk
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // chunk size
  view.setUint16(20, 1, true) // PCM format
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true) // byte rate
  view.setUint16(32, numChannels * bytesPerSample, true) // block align
  view.setUint16(34, bitDepth, true) // bits per sample

  // data chunk
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  // Interleaved sample data (little-endian)
  let offset = headerSize
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = clampSample(channels[ch][i])
      offset = writeSampleLE(view, offset, sample, bitDepth)
    }
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

// ---------------------------------------------------------------------------
// AIFF (FORM, big-endian)
// ---------------------------------------------------------------------------

function encodeAiff({ bitDepth, sampleRate, channels }: EncodeOptions): Blob {
  const numChannels = channels.length
  const numSamples = channels[0].length
  const bytesPerSample = bitDepth / 8
  const ssndDataSize = numSamples * numChannels * bytesPerSample
  // SSND chunk: 8 (offset + blockSize) + sample data
  const ssndChunkSize = 8 + ssndDataSize
  // COMM chunk: 18 bytes
  const commChunkSize = 18
  // FORM size: 4 (AIFF) + 8+commChunkSize + 8+ssndChunkSize
  const formSize = 4 + (8 + commChunkSize) + (8 + ssndChunkSize)
  const buffer = new ArrayBuffer(12 + formSize - 4)
  const view = new DataView(buffer)

  // FORM header
  writeString(view, 0, 'FORM')
  view.setUint32(4, formSize, false) // big-endian
  writeString(view, 8, 'AIFF')

  // COMM chunk
  let pos = 12
  writeString(view, pos, 'COMM')
  view.setUint32(pos + 4, commChunkSize, false)
  view.setInt16(pos + 8, numChannels, false)
  view.setUint32(pos + 10, numSamples, false)
  view.setInt16(pos + 14, bitDepth, false)
  // Sample rate as 80-bit IEEE 754 extended — write simplified version
  writeExtended(view, pos + 16, sampleRate)
  pos += 8 + commChunkSize

  // SSND chunk
  writeString(view, pos, 'SSND')
  view.setUint32(pos + 4, ssndChunkSize, false)
  view.setUint32(pos + 8, 0, false) // offset
  view.setUint32(pos + 12, 0, false) // blockSize
  pos += 16

  // Interleaved sample data (big-endian)
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = clampSample(channels[ch][i])
      pos = writeSampleBE(view, pos, sample, bitDepth)
    }
  }

  return new Blob([buffer], { type: 'audio/aiff' })
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampSample(f: number): number {
  return Math.max(-1, Math.min(1, f))
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}

function writeSampleLE(view: DataView, offset: number, sample: number, bitDepth: BitDepth): number {
  if (bitDepth === 16) {
    const val = Math.round(sample * 32767)
    view.setInt16(offset, val, true)
    return offset + 2
  }
  if (bitDepth === 24) {
    const val = Math.round(sample * 8388607)
    view.setUint8(offset, val & 0xff)
    view.setUint8(offset + 1, (val >> 8) & 0xff)
    view.setUint8(offset + 2, (val >> 16) & 0xff)
    return offset + 3
  }
  // 32-bit
  const val = Math.round(sample * 2147483647)
  view.setInt32(offset, val, true)
  return offset + 4
}

function writeSampleBE(view: DataView, offset: number, sample: number, bitDepth: BitDepth): number {
  if (bitDepth === 16) {
    const val = Math.round(sample * 32767)
    view.setInt16(offset, val, false)
    return offset + 2
  }
  if (bitDepth === 24) {
    const val = Math.round(sample * 8388607)
    view.setUint8(offset, (val >> 16) & 0xff)
    view.setUint8(offset + 1, (val >> 8) & 0xff)
    view.setUint8(offset + 2, val & 0xff)
    return offset + 3
  }
  // 32-bit
  const val = Math.round(sample * 2147483647)
  view.setInt32(offset, val, false)
  return offset + 4
}

/**
 * Write a sample rate as an 80-bit IEEE 754 extended precision float.
 * This is a simplified encoder that works for common sample rates.
 */
function writeExtended(view: DataView, offset: number, rate: number) {
  // 80-bit extended: 1 sign, 15 exponent (bias 16383), 64 mantissa (explicit integer bit)
  let exponent = 16383 + 63
  let mantissa = rate
  // Normalise so mantissa fits in 64-bit integer range
  while (mantissa < 4503599627370496) {
    // 2^52
    mantissa *= 2
    exponent--
  }
  while (mantissa >= 9007199254740992) {
    // 2^53
    mantissa /= 2
    exponent++
  }
  // Re-normalise for the 64-bit mantissa field
  // The extended format stores an explicit integer bit
  exponent = 16383
  let f = rate
  if (f === 0) {
    for (let i = 0; i < 10; i++) view.setUint8(offset + i, 0)
    return
  }
  // Find exponent
  let exp = 0
  while (f >= 2) {
    f /= 2
    exp++
  }
  while (f < 1) {
    f *= 2
    exp--
  }
  exponent += exp
  // f is now in [1, 2), convert to 64-bit mantissa
  const high = Math.floor(f * 2147483648) // 2^31
  const frac = f * 2147483648 - high
  const low = Math.floor(frac * 4294967296) // 2^32
  view.setUint16(offset, exponent, false)
  view.setUint32(offset + 2, high, false)
  view.setUint32(offset + 6, low, false)
}

export {}
